import React, { useState } from "react";
import styled from "styled-components";
import { skills } from "../../data/constants";
import {
  FiLayout,
  FiServer,
  FiDatabase,
  FiCode,
  FiTool,
  FiLayers,
} from "react-icons/fi";

const Container = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  position: relative;
  z-index: 1;
  align-items: center;
  padding: 60px 0 80px 0;
  @media (max-width: 960px) {
    padding: 40px 0 60px 0;
  }
`;

const Wrapper = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  max-width: 1150px;
  gap: 16px;
  padding: 0 20px;
`;

const Tag = styled.div`
  background: rgba(133, 76, 230, 0.12);
  border: 1px solid rgba(133, 76, 230, 0.4);
  color: #c084fc;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  padding: 6px 18px;
  border-radius: 9999px;
  box-shadow: 0 0 15px rgba(133, 76, 230, 0.2);
  display: inline-flex;
  align-items: center;
  justify-content: center;
`;

const Title = styled.h2`
  font-size: 38px;
  font-weight: 700;
  text-align: center;
  color: ${({ theme }) => theme.text_primary};
  margin: 0;
  letter-spacing: -0.02em;

  @media (max-width: 768px) {
    font-size: 30px;
  }
`;

const SubTitle = styled.p`
  font-size: 16px;
  text-align: center;
  max-width: 650px;
  color: rgba(255, 255, 255, 0.65);
  line-height: 1.6;
  margin: 0 0 12px 0;

  @media (max-width: 768px) {
    font-size: 14.5px;
  }
`;

/* Filter tabs for professional interaction */
const FilterTabs = styled.div`
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 20px;
  width: 100%;
`;

const TabButton = styled.button`
  background: ${(props) =>
    props.active
      ? "linear-gradient(135deg, #854CE6 0%, #a855f7 100%)"
      : "rgba(255, 255, 255, 0.04)"};
  border: 1px solid
    ${(props) =>
      props.active ? "#854CE6" : "rgba(255, 255, 255, 0.08)"};
  color: ${(props) => (props.active ? "#ffffff" : "rgba(255, 255, 255, 0.7)")};
  padding: 8px 18px;
  border-radius: 12px;
  font-size: 13.5px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.25s ease;
  display: flex;
  align-items: center;
  gap: 8px;
  box-shadow: ${(props) =>
    props.active ? "0 4px 20px rgba(133, 76, 230, 0.35)" : "none"};

  &:hover {
    background: ${(props) =>
      props.active
        ? "linear-gradient(135deg, #854CE6 0%, #a855f7 100%)"
        : "rgba(133, 76, 230, 0.12)"};
    border-color: rgba(133, 76, 230, 0.4);
    color: #ffffff;
  }

  svg {
    font-size: 15px;
  }
`;

const SkillsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(340px, 1fr));
  gap: 24px;
  width: 100%;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 18px;
  }
`;

const SkillCategoryCard = styled.div`
  background: ${({ theme }) => theme.card || "#171721"};
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 20px;
  padding: 28px 24px;
  display: flex;
  flex-direction: column;
  gap: 20px;
  transition: all 0.35s ease;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.25);
  position: relative;

  &:hover {
    border-color: rgba(133, 76, 230, 0.5);
    transform: translateY(-4px);
    box-shadow: 0 10px 32px rgba(133, 76, 230, 0.16);
  }

  @media (max-width: 640px) {
    padding: 22px 18px;
  }
`;

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-bottom: 14px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const CategoryIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: rgba(133, 76, 230, 0.15);
  border: 1px solid rgba(133, 76, 230, 0.35);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #c084fc;
  font-size: 20px;
  box-shadow: 0 0 12px rgba(133, 76, 230, 0.2);
`;

const CategoryTitle = styled.h3`
  font-size: 19px;
  font-weight: 700;
  color: #ffffff;
  margin: 0;
  letter-spacing: -0.01em;
`;

const ItemCountBadge = styled.span`
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.08);
  color: rgba(255, 255, 255, 0.5);
  font-size: 11.5px;
  font-weight: 600;
  padding: 4px 10px;
  border-radius: 9999px;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const SkillsList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
`;

const SkillBadge = styled.div`
  background: rgba(255, 255, 255, 0.035);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 12px;
  padding: 9px 14px;
  display: flex;
  align-items: center;
  gap: 9px;
  font-size: 13.5px;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.85);
  transition: all 0.25s ease;
  user-select: none;

  &:hover {
    background: rgba(133, 76, 230, 0.12);
    border-color: rgba(133, 76, 230, 0.45);
    color: #ffffff;
    transform: translateY(-2px);
    box-shadow: 0 4px 14px rgba(133, 76, 230, 0.2);
  }

  @media (max-width: 640px) {
    font-size: 12.5px;
    padding: 7px 11px;
    gap: 7px;
  }
`;

const SkillIconWrapper = styled.div`
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    filter: drop-shadow(0 0 4px rgba(0, 0, 0, 0.4));
  }
`;

const getCategoryIcon = (title) => {
  switch (title.toLowerCase()) {
    case "frontend":
      return <FiLayout />;
    case "backend":
      return <FiServer />;
    case "databases":
      return <FiDatabase />;
    case "programming languages":
      return <FiCode />;
    case "tools & platforms":
      return <FiTool />;
    default:
      return <FiLayers />;
  }
};

const Skills = () => {
  const [activeTab, setActiveTab] = useState("all");

  const tabs = [
    { id: "all", label: "All Skills", icon: <FiLayers /> },
    { id: "frontend", label: "Frontend", icon: <FiLayout /> },
    { id: "backend", label: "Backend", icon: <FiServer /> },
    { id: "databases", label: "Databases", icon: <FiDatabase /> },
    { id: "programming languages", label: "Languages", icon: <FiCode /> },
    { id: "tools & platforms", label: "Tools", icon: <FiTool /> },
  ];

  const filteredSkills =
    activeTab === "all"
      ? skills
      : skills.filter((item) => item.title.toLowerCase() === activeTab);

  return (
    <Container id="skills">
      <Wrapper>
        <Tag>Tech Arsenal</Tag>
        <Title>Skills & Expertise</Title>
        <SubTitle>
          A comprehensive overview of my technical stack, modern frameworks,
          database architectures, and development tools.
        </SubTitle>

        {/* Interactive Filter Tabs */}
        <FilterTabs>
          {tabs.map((tab) => (
            <TabButton
              key={tab.id}
              active={activeTab === tab.id}
              onClick={() => setActiveTab(tab.id)}
            >
              {tab.icon}
              <span>{tab.label}</span>
            </TabButton>
          ))}
        </FilterTabs>

        {/* Skills Cards Grid */}
        <SkillsGrid>
          {filteredSkills.map((category, idx) => (
            <SkillCategoryCard key={idx}>
              <CardHeader>
                <HeaderLeft>
                  <CategoryIcon>{getCategoryIcon(category.title)}</CategoryIcon>
                  <CategoryTitle>{category.title}</CategoryTitle>
                </HeaderLeft>
                <ItemCountBadge>
                  {category.skills.length} Skills
                </ItemCountBadge>
              </CardHeader>

              <SkillsList>
                {category.skills.map((item, sIdx) => (
                  <SkillBadge key={sIdx}>
                    <SkillIconWrapper>
                      <img
                        src={item.image}
                        alt={item.name}
                        loading="lazy"
                        onError={(e) => {
                          e.target.style.display = "none";
                        }}
                      />
                    </SkillIconWrapper>
                    <span>{item.name}</span>
                  </SkillBadge>
                ))}
              </SkillsList>
            </SkillCategoryCard>
          ))}
        </SkillsGrid>
      </Wrapper>
    </Container>
  );
};

export default Skills;